import type {ComponentStageData, PluginContext, ReadOnlyExtensionAPI, StageHandler} from '../../../../types'
import {
    getMeasurementsPointer,
    getStructurePointer,
    getTargetLayoutPointer
} from '../../../../conversionDal/utils/pointer'
import {updateTargetLayout} from '../../../common'
import {generateChildLayout, getContainerRowCountKey, getOrderStrategy} from '../../common'
import {deepClone} from '@wix/wix-immutable-proxy'

const getUsedRows = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>
): Set<number> => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)
    const usedRows = new Set<number>()

    if (!component?.components) {
        return usedRows
    }

    for (const childId of component.components) {
        const childPointer = getStructurePointer({id: childId})
        const childLayout = deepClone(conversionDal.get(getTargetLayoutPointer(childPointer)))

        if (childLayout?.itemLayout?.gridArea) {
            const {rowStart, rowEnd} = childLayout.itemLayout.gridArea
            for (let row = rowStart; row < rowEnd; row++) {
                usedRows.add(row)
            }
        }
    }

    return usedRows
}

const optimizeEmptyRowsAndShiftComponents = (
    componentStageData: Pick<ComponentStageData, 'componentPointer' | 'conversionDal'>,
    currentRowCount: number
): {optimizedRowCount: number; emptyRows: number[]} => {
    const {componentPointer, conversionDal} = componentStageData
    const component = conversionDal.get(componentPointer)
    const usedRows = getUsedRows(componentStageData)

    if (!component?.components || usedRows.size === 0) {
        return {optimizedRowCount: 0, emptyRows: []}
    }

    const maxUsedRow = Math.max(...Array.from(usedRows))

    const middleEmptyRows: number[] = []
    for (let i = 1; i <= maxUsedRow; i++) {
        if (!usedRows.has(i)) {
            middleEmptyRows.push(i)
        }
    }

    const trailingEmptyRows: number[] = []
    for (let i = maxUsedRow + 1; i <= currentRowCount; i++) {
        trailingEmptyRows.push(i)
    }

    const emptyRows = [...middleEmptyRows, ...trailingEmptyRows]

    if (emptyRows.length === 0) {
        return {optimizedRowCount: maxUsedRow, emptyRows: []}
    }

    const rowMapping = new Map<number, number>()
    let newRowIndex = 1

    for (let oldRow = 1; oldRow <= maxUsedRow; oldRow++) {
        if (!middleEmptyRows.includes(oldRow)) {
            rowMapping.set(oldRow, newRowIndex)
            newRowIndex++
        }
    }

    for (const childId of component.components) {
        const childPointer = getStructurePointer({id: childId})
        const childLayout = deepClone(conversionDal.get(getTargetLayoutPointer(childPointer)))

        if (childLayout?.itemLayout?.gridArea) {
            const {rowStart, rowEnd} = childLayout.itemLayout.gridArea
            const newRowStart = rowMapping.get(rowStart)
            const newRowEnd = rowMapping.get(rowEnd - 1)

            if (newRowStart !== undefined && newRowEnd !== undefined) {
                updateTargetLayout(
                    {conversionDal, componentPointer: childPointer},
                    {
                        itemLayout: {
                            ...childLayout.itemLayout,
                            gridArea: {
                                ...childLayout.itemLayout.gridArea,
                                rowStart: newRowStart,
                                rowEnd: newRowEnd + 1
                            }
                        }
                    }
                )
            }
        }
    }

    return {optimizedRowCount: maxUsedRow - middleEmptyRows.length, emptyRows}
}

export const createOrderRealStructure: (
    readOnlyExtensionAPI: ReadOnlyExtensionAPI
) => StageHandler<ComponentStageData> =
    readOnlyExtensionAPI =>
    ({componentPointer, conversionDal, initialSnapshot}: ComponentStageData) => {
        const component = conversionDal.get(componentPointer)

        if (!component?.components || component?.type !== 'Container' || component.isSlottedComponent) {
            return
        }

        const layoutIntent = component?.layoutIntent
        const isRtl = readOnlyExtensionAPI.isRTL()
        const {compareFunction, appliedHeuristics} = getOrderStrategy(layoutIntent, isRtl)
        const sortCallback = (childIdA: string, childIdB: string) => {
            const childALayout = deepClone(initialSnapshot.getValue(getTargetLayoutPointer({id: childIdA})))
            const childBLayout = deepClone(initialSnapshot.getValue(getTargetLayoutPointer({id: childIdB})))

            if (childALayout && childBLayout) {
                return (
                    childALayout.itemLayout.gridArea.rowStart - childBLayout.itemLayout.gridArea.rowStart ||
                    (childBLayout.itemLayout.margins?.bottom?.value || 0) -
                        (childALayout.itemLayout.margins?.bottom?.value || 0)
                )
            }

            if (childALayout) {
                return -1
            }

            if (childBLayout) {
                return 1
            }

            return compareFunction(
                conversionDal.get(getMeasurementsPointer({id: childIdA})),
                conversionDal.get(getMeasurementsPointer({id: childIdB}))
            )
        }

        conversionDal.modify(componentPointer, value => ({
            ...value,
            components: [...value.components].sort(sortCallback)
        }))

        return {appliedHeuristics}
    }

export const positionItems = (
    {componentPointer, conversionDal, initialSnapshot}: ComponentStageData,
    contextHelper?: PluginContext
) => {
    const component = conversionDal.get(componentPointer)

    if (!component.components?.length || component?.type !== 'Container' || component.isSlottedComponent) {
        return
    }

    let startRow = 1
    const {containerLayout} = deepClone(initialSnapshot.getValue(getTargetLayoutPointer(componentPointer))) ?? {}
    const columnCount = containerLayout?.columns?.length ?? 1
    for (const childId of component.components ?? []) {
        const childPointer = getStructurePointer({id: childId})
        const layout = deepClone(initialSnapshot.getValue(getTargetLayoutPointer(childPointer)))

        if (layout?.itemLayout?.gridArea?.rowEnd) {
            startRow = Math.max(startRow, layout.itemLayout.gridArea.rowEnd)
            continue
        }

        updateTargetLayout(
            {conversionDal, componentPointer: childPointer},
            generateChildLayout(startRow, 1, columnCount + 1)
        )
        startRow++
    }

    const currentRows = containerLayout?.rows ?? []

    const {optimizedRowCount, emptyRows} = optimizeEmptyRowsAndShiftComponents(
        {componentPointer, conversionDal},
        currentRows.length
    )

    if (emptyRows.length > 0) {
        const newRows: any[] = []

        for (let i = 0; i < currentRows.length; i++) {
            const rowIndex = i + 1

            if (!emptyRows.includes(rowIndex)) {
                newRows.push(currentRows[i])
            }
        }

        updateTargetLayout(
            {conversionDal, componentPointer},
            {
                containerLayout: {
                    ...containerLayout,
                    rows: newRows,
                    type: 'GridContainerLayout'
                }
            }
        )
    }

    const rowCountKey = getContainerRowCountKey(componentPointer)
    contextHelper!.set(rowCountKey, optimizedRowCount)
}
